import { useCallback, useRef } from 'react';
import { Canvas, FabricImage } from 'fabric';
import { applyVideoControls } from '../utils/video-controls';
import { createOwnLaneKey } from '../../timeline/utils/timeline-rows';

type RegisterVideo = (id: string, element: HTMLVideoElement) => void;
type TrackMediaLoad = <T>(promise: Promise<T>) => Promise<T>;
type FitMode = 'contain' | 'cover';

/**
 * Adds an uploaded video to the canvas.
 * Keeps the editor in a loading state until the video is ready to play.
 *
 * Scales against the design dimensions so objects are placed in the same
 * coordinate space used by the rest of the editor (and by export).
 */
export function useVideoUpload(
  canvas: Canvas | null,
  registerVideo: RegisterVideo,
  trackMediaLoad: TrackMediaLoad | undefined,
  designWidth: number,
  designHeight: number,
  fitMode: FitMode = 'contain'
) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!canvas) return;
      const file = event.target.files?.[0];
      if (!file) return;
      event.target.value = '';

      const url = URL.createObjectURL(file);
      const videoElement = document.createElement('video');
      videoElement.src = url;
      videoElement.crossOrigin = 'anonymous';
      videoElement.muted = true;
      videoElement.preload = 'auto';

      const load = new Promise<void>((resolve, reject) => {
        videoElement.addEventListener(
          'canplaythrough',
          () => {
            const videoWidth = videoElement.videoWidth;
            const videoHeight = videoElement.videoHeight;
            videoElement.width = videoWidth;
            videoElement.height = videoHeight;

            const containScale = Math.min(designWidth / videoWidth, designHeight / videoHeight);
            const coverScale = Math.max(designWidth / videoWidth, designHeight / videoHeight);
            const scale = fitMode === 'cover' ? Math.min(coverScale, containScale * 1.5) : containScale;
            const videoId = `video_${Date.now()}`;

            const fabricImage = new FabricImage(videoElement, {
              left: Math.round(designWidth / 2),
              top: Math.round(designHeight / 2),
              originX: 'center',
              originY: 'center',
              scaleX: scale,
              scaleY: scale,
              selectable: true,
              objectCaching: false
            });
            fabricImage.set('id', videoId);
            applyVideoControls(fabricImage);

            // Store the original video URL so the model can persist it.
            fabricImage.videoSrc = url;
            // Own track-row, kept across save/reload, rendered at its natural (front-most) position
            // — see timeline/utils/timeline-rows.ts.
            fabricImage.laneKey = createOwnLaneKey();

            registerVideo(videoId, videoElement);
            canvas.add(fabricImage);
            videoElement.currentTime = 0;
            resolve();
          },
          { once: true }
        );
        videoElement.addEventListener('error', () => reject(new Error('Video upload failed')), { once: true });
      });

      (trackMediaLoad ? trackMediaLoad(load) : load).catch(() => undefined);
    },
    [canvas, registerVideo, trackMediaLoad, designWidth, designHeight, fitMode]
  );

  const open = useCallback(() => inputRef.current?.click(), []);

  return { open, inputRef, handleChange };
}
